import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Sparkles, Send, Plus, Pin, Trash2, Wind, BarChart3, TrendingUp, Zap, AlertTriangle, Globe, Bot, User, Loader2, History, X } from 'lucide-react';
import { useChatHistory } from '../hooks/useChatHistory';
import { cn } from '../components/ui/Button';
import { RichMessageContent } from '../components/ai/InlineChatWidgets';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { LayoutGrid, Star, Clock } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
// Reuse suggestions from AiChatPanel
const SUGGESTIONS = [{
  icon: Wind,
  label: 'Run delta analysis',
  prompt: 'Show me a run delta comparison table for the latest EPT-2, EC AIFS, and EC IFS model runs. Compare 06z vs 12z for wind 100m and temperature 2m across Central Europe. Flag any significant shifts.'
}, {
  icon: BarChart3,
  label: 'Model divergence',
  prompt: 'Build a model divergence matrix for the next 5 days. Compare EPT-2, EC AIFS, EC IFS, and GFS for wind generation in Germany. Flag any spreads >15% as trading signals.'
}, {
  icon: TrendingUp,
  label: 'Price spread opps',
  prompt: 'Analyze day-ahead vs intraday price spreads across DE, FR, NL, and NO2. Identify the top 3 arbitrage opportunities driven by weather forecast updates.'
}, {
  icon: Zap,
  label: 'Wind generation',
  prompt: 'Multi-model wind generation outlook for Germany and North Sea D+0 through D+5. Show capacity factors, ramp events, and compare EPT-2 vs EC IFS in a table.'
}, {
  icon: AlertTriangle,
  label: 'Cross-border flows',
  prompt: 'Analyze cross-border electricity flows and interconnector utilization for DE-FR, DE-NL, NO2-DE, and DE-AT. Show price differentials and flow direction opportunities.'
}, {
  icon: Globe,
  label: 'Balancing signals',
  prompt: 'What are the current balancing market signals for Central Europe? Show imbalance volumes, net positions, and flag upcoming ramp risks from renewable intermittency.'
}];
// Parse multiple-choice options from assistant message (reused logic)
function parseChoices(content: string): {
  text: string;
  choices: {
    key: string;
    label: string;
  }[];
} | null {
  const choiceRegex = /\[([A-D])\]\s*(.+)/g;
  const choices: {
    key: string;
    label: string;
  }[] = [];
  let match;
  while ((match = choiceRegex.exec(content)) !== null) {
    choices.push({
      key: match[1],
      label: match[2].trim()
    });
  }
  if (choices.length >= 2) {
    const textWithoutChoices = content.replace(/\[([A-D])\]\s*(.+)/g, '').trim();
    return {
      text: textWithoutChoices,
      choices
    };
  }
  return null;
}
export function AthenaView() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    workspaces,
    setActiveWorkspace
  } = useWorkspaces();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const [showAthenaHint, setShowAthenaHint] = useState(false);
  const hintDismissedRef = useRef(false);
  // Show the Athena hint after the product tour completes
  useEffect(() => {
    const dismissed = localStorage.getItem('jua-athena-hint-dismissed') === 'true';
    if (dismissed) {
      hintDismissedRef.current = true;
      return;
    }
    const check = () => {
      if (hintDismissedRef.current) return;
      const tourDone = localStorage.getItem('jua-tour-completed') === 'true';
      if (tourDone) {
        // Small delay so it feels like a follow-up, not simultaneous
        setTimeout(() => {
          if (!hintDismissedRef.current) {
            setShowAthenaHint(true);
          }
        }, 600);
      }
    };
    check();
    // Also listen for storage changes (tour completing)
    window.addEventListener('storage', check);
    // Poll briefly in case the tour just finished in this tab
    const interval = setInterval(check, 1000);
    return () => {
      window.removeEventListener('storage', check);
      clearInterval(interval);
    };
  }, []);
  const dismissAthenaHint = () => {
    hintDismissedRef.current = true;
    setShowAthenaHint(false);
    localStorage.setItem('jua-athena-hint-dismissed', 'true');
  };
  const {
    chats,
    activeChatId,
    setActiveChatId,
    sendMessage,
    handleNewChat,
    togglePin,
    deleteChat,
    isStreaming
  } = useChatHistory();
  const [inputValue, setInputValue] = useState('');
  // Prefill input from navigation state (e.g. from onboarding or Athena Discovery)
  const prefillHandled = useRef(false);
  useEffect(() => {
    const state = location.state as {
      initialPrompt?: string;
    } | null;
    if (state?.initialPrompt && !prefillHandled.current) {
      prefillHandled.current = true;
      setInputValue(state.initialPrompt);
      // Clear the state so refreshing doesn't re-prefill
      navigate(location.pathname, {
        replace: true,
        state: {}
      });
    }
  }, [location.state]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const welcomeTextareaRef = useRef<HTMLTextAreaElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const activeChat = chats.find(c => c.id === activeChatId);
  const pinnedChats = chats.filter(c => c.pinned);
  const recentChats = chats.filter(c => !c.pinned);
  const pinnedWorkspaces = workspaces.filter(w => w.pinned || w.favorited).slice(0, 4);
  const recentWorkspaces = workspaces.filter(w => !w.pinned && !w.favorited).slice(0, 4);
  const displayWorkspaces = pinnedWorkspaces.length > 0 ? pinnedWorkspaces : recentWorkspaces;
  // Close history dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (historyOpen && historyPanelRef.current && !historyPanelRef.current.contains(e.target as Node) && historyButtonRef.current && !historyButtonRef.current.contains(e.target as Node)) {
        setHistoryOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [historyOpen]);
  // Scroll to bottom on new messages — only if user is near the bottom
  const prevMessageCountRef = useRef(0);
  const userScrolledUpRef = useRef(false);
  // Track manual scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledUpRef.current = distanceFromBottom > 200;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeChat?.id]);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !messagesEndRef.current) return;
    const messageCount = activeChat?.messages?.length || 0;
    const isNewMessage = messageCount > prevMessageCountRef.current;
    prevMessageCountRef.current = messageCount;
    // Only auto-scroll if a new message was added AND user hasn't scrolled up
    if (isNewMessage && !userScrolledUpRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [activeChat?.messages?.length]);
  // Reset scroll tracking when switching chats
  useEffect(() => {
    userScrolledUpRef.current = false;
    prevMessageCountRef.current = activeChat?.messages?.length || 0;
  }, [activeChat?.id]);
  const handleSend = async (content?: string) => {
    const text = content || inputValue;
    if (!text.trim() || isStreaming) return;
    setInputValue('');
    // Intercept discovery prompts to launch the discovery workspace
    const lower = text.toLowerCase();
    if (lower.includes('show me what you can do') || lower.includes('what can athena do')) {
      navigate('/workspace/athena-discovery', {
        state: {
          openAiPanel: true
        }
      });
      return;
    }
    await sendMessage(text, {
      name: 'Overview',
      description: 'The user is on the Overview/Home page with the Athena AI energy trading intelligence interface.'
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isStreaming) {
        handleSend();
      }
    }
  };
  // Auto-resize textareas
  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    el.style.overflowY = el.scrollHeight > 200 ? 'auto' : 'hidden';
  };
  useEffect(() => {
    autoResize(welcomeTextareaRef.current);
    autoResize(chatTextareaRef.current);
  }, [inputValue]);
  // Keyboard shortcut for A/B/C/D selection
  useEffect(() => {
    const lastMessage = activeChat?.messages[activeChat.messages.length - 1];
    const lastMessageChoices = lastMessage?.role === 'assistant' && lastMessage.content ? parseChoices(lastMessage.content) : null;
    if (!lastMessageChoices || isStreaming) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const key = e.key.toUpperCase();
      const choice = lastMessageChoices.choices.find(c => c.key === key);
      if (choice) {
        e.preventDefault();
        handleSend(`${choice.key}: ${choice.label}`);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeChat, isStreaming]);
  const renderMessageContent = (msg: any, isLastAssistant: boolean) => {
    if (!msg.content) {
      return <span className="flex items-center gap-1" data-id="element-2945">
          <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:-0.3s]" data-id="element-2946" />
          <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:-0.15s]" data-id="element-2947" />
          <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" data-id="element-2948" />
        </span>;
    }
    if (msg.role === 'assistant') {
      // Check for choices first
      if (isLastAssistant && !isStreaming) {
        const parsed = parseChoices(msg.content);
        if (parsed) {
          return <div className="space-y-3" data-id="element-2949">
              <RichMessageContent content={parsed.text} data-id="element-2950" />
              <div className="space-y-1.5" data-id="element-2951">
                {parsed.choices.map(choice => <button key={choice.key} onClick={() => handleSend(`${choice.key}: ${choice.label}`)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:bg-[#EC4899]/10 hover:border-[#EC4899]/30 transition-all text-left group" data-id="element-2952">
                    <span className="w-6 h-6 rounded-md bg-white/[0.06] border border-white/[0.1] flex items-center justify-center text-[11px] font-bold text-white/50 group-hover:text-[#EC4899] group-hover:border-[#EC4899]/30 group-hover:bg-[#EC4899]/10 transition-colors flex-shrink-0" data-id="element-2953">
                      {choice.key}
                    </span>
                    <span className="text-white/70 group-hover:text-white/90 transition-colors text-[13px]" data-id="element-2954">
                      {choice.label}
                    </span>
                  </button>)}
              </div>
            </div>;
        }
      }
      // Use RichMessageContent for all assistant messages
      return <RichMessageContent content={msg.content} data-id="element-2955" />;
    }
    return <span className="whitespace-pre-wrap" data-id="element-2956">{msg.content}</span>;
  };
  return <div className="jua-overview-page jua-page-bg flex flex-col h-[calc(100vh-64px)] overflow-hidden relative" data-id="element-2957">
      {/* Top bar with History dropdown — only shown when chat is active */}
      {activeChat && <div className="flex items-center justify-end gap-2 px-4 pt-3 pb-1 flex-shrink-0" data-id="element-2958">
          <button onClick={() => {
        handleNewChat();
        setHistoryOpen(false);
      }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EC4899]/10 text-[#EC4899] text-xs font-medium hover:bg-[#EC4899]/20 border border-[#EC4899]/20 transition-colors" data-id="element-2959">
            <Plus size={14} data-id="element-2960" /> New Chat
          </button>

          <div className="relative" data-id="element-2961">
            <button ref={historyButtonRef} onClick={() => setHistoryOpen(!historyOpen)} className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors', isLight ? historyOpen ? 'bg-black/[0.08] text-gray-800 border-black/[0.12]' : 'bg-black/[0.04] text-gray-600 border-black/[0.08] hover:bg-black/[0.07] hover:text-gray-900' : historyOpen ? 'bg-white/[0.1] text-white border-white/[0.15]' : 'bg-white/[0.04] text-white/60 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/90')} data-id="element-2962">
              <History size={14} data-id="element-2963" />
              History
              {chats.length > 0 && <span className={cn('ml-1 w-4 h-4 rounded-full text-[10px] flex items-center justify-center', isLight ? 'bg-black/[0.08] text-gray-500' : 'bg-white/[0.1] text-white/50')} data-id="element-2964">
                  {chats.length}
                </span>}
            </button>

            {historyOpen && ReactDOM.createPortal(<div ref={historyPanelRef} className="fixed w-72 max-h-[480px] rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden flex flex-col" style={{
          background: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          zIndex: 9999,
          top: historyButtonRef.current ? historyButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
          right: 16
        }} data-id="element-2965">
                  <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
          }} data-id="element-2966" />

                  <div className="relative flex items-center justify-between px-4 py-3 border-b border-white/[0.06]" data-id="element-2967">
                    <span className="text-sm font-semibold text-white/90" data-id="element-2968">
                      Chat History
                    </span>
                    <button onClick={() => setHistoryOpen(false)} className="p-1 text-white/40 hover:text-white/80 hover:bg-white/[0.08] rounded-md transition-colors" data-id="element-2969">
                      <X size={14} data-id="element-2970" />
                    </button>
                  </div>

                  <div className="relative flex-1 overflow-y-auto p-2 space-y-3" data-id="element-2971">
                    {pinnedChats.length > 0 && <div data-id="element-2972">
                        <div className="px-2 py-1 text-[10px] font-semibold text-white/30 uppercase tracking-wider flex items-center gap-1" data-id="element-2973">
                          <Pin size={8} data-id="element-2974" /> Pinned
                        </div>
                        <div className="space-y-0.5 mt-1" data-id="element-2975">
                          {pinnedChats.map(chat => <div key={chat.id} onClick={() => {
                  setActiveChatId(chat.id);
                  setHistoryOpen(false);
                }} className={cn('group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors', activeChatId === chat.id ? 'bg-white/[0.1] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-2976">
                              <span className="truncate text-xs" data-id="element-2977">
                                {chat.title}
                              </span>
                              <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2978">
                                <button onClick={e => {
                      e.stopPropagation();
                      togglePin(chat.id);
                    }} className="p-1 hover:bg-white/[0.1] rounded text-[#EC4899]" data-id="element-2979">
                                  <Pin size={10} className="fill-current" data-id="element-2980" />
                                </button>
                              </div>
                            </div>)}
                        </div>
                      </div>}

                    {recentChats.length > 0 && <div data-id="element-2981">
                        <div className="px-2 py-1 text-[10px] font-semibold text-white/30 uppercase tracking-wider" data-id="element-2982">
                          Recent
                        </div>
                        <div className="space-y-0.5 mt-1" data-id="element-2983">
                          {recentChats.map(chat => <div key={chat.id} onClick={() => {
                  setActiveChatId(chat.id);
                  setHistoryOpen(false);
                }} className={cn('group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors', activeChatId === chat.id ? 'bg-white/[0.1] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-2984">
                              <span className="truncate text-xs" data-id="element-2985">
                                {chat.title}
                              </span>
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2986">
                                <button onClick={e => {
                      e.stopPropagation();
                      togglePin(chat.id);
                    }} className="p-1 hover:bg-white/[0.1] rounded text-white/40 hover:text-white" data-id="element-2987">
                                  <Pin size={10} data-id="element-2988" />
                                </button>
                                <button onClick={e => {
                      e.stopPropagation();
                      deleteChat(chat.id);
                    }} className="p-1 hover:bg-white/[0.1] rounded text-white/40 hover:text-red-400" data-id="element-2989">
                                  <Trash2 size={10} data-id="element-2990" />
                                </button>
                              </div>
                            </div>)}
                        </div>
                      </div>}

                    {chats.length === 0 && <div className="px-3 py-6 text-center text-xs text-white/30" data-id="element-2991">
                        No chat history yet
                      </div>}
                  </div>
                </div>, document.body)}
          </div>
        </div>}

      {/* Main Content — full width */}
      <div className="flex-1 flex flex-col relative min-h-0" data-id="element-2992">
        {!activeChat ?
      // Welcome State
      <div className="flex-1 overflow-y-auto" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(236,72,153,0.18) 0%, rgba(168,85,247,0.10) 35%, rgba(99,102,241,0.07) 55%, transparent 75%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(56,189,248,0.07) 0%, rgba(168,85,247,0.05) 40%, transparent 70%), radial-gradient(ellipse 50% 40% at 10% 90%, rgba(236,72,153,0.08) 0%, transparent 60%)'
      }} data-id="element-2993">
            <div className="flex flex-col items-center min-h-full px-8 py-4 md:py-8 max-w-4xl mx-auto w-full pt-6 md:pt-12" data-id="element-2994">
              <div className="flex-1 flex flex-col items-center justify-start md:justify-center w-full pt-4 md:pt-0" data-id="element-2995">
                <div className="jua-athena-welcome flex flex-col items-center w-full" data-id="element-2996">
                  <div className="w-16 h-16 flex items-center justify-center mb-5" data-id="element-2997">
                    <img src="https://cdn.magicpatterns.com/uploads/6Vk965L7QqZBGFchHxJDat/pasted-image.png" alt="Jua logo" className="w-16 h-16 object-contain" data-id="element-2998" />
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2 text-center" data-id="element-2999">
                    I'm Athena, how can I help?
                  </h1>
                  <p className="text-slate-400 mb-7 text-center max-w-lg text-base" data-id="element-3000">
                    Ask about weather patterns, energy markets, or analyze
                    specific data points.
                  </p>

                  {/* Big input field for empty state */}
                  <div className="w-full max-w-2xl mb-5 relative" data-id="element-3001">
                    <textarea ref={welcomeTextareaRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={isStreaming ? 'Athena is thinking...' : 'Ask Athena anything...'} rows={4} className="jua-overview-main-input w-full min-h-[120px] max-h-[200px] px-6 pr-14 py-4 rounded-2xl bg-white/[0.06] border border-white/[0.10] text-base text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[#EC4899]/40 focus:ring-2 focus:ring-[#EC4899]/10 transition-all shadow-xl shadow-black/10 resize-none overflow-hidden leading-relaxed backdrop-blur-sm" data-id="element-3002" />
                    <button onClick={() => handleSend()} disabled={!inputValue.trim() || isStreaming} className="absolute right-3 bottom-3 p-2.5 text-[#EC4899] hover:bg-[#EC4899]/15 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-3003">
                      {isStreaming ? <Loader2 size={20} className="animate-spin" data-id="element-3004" /> : <Send size={20} data-id="element-3005" />}
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 w-full max-w-2xl" data-id="element-3006">
                    <div className="relative" data-id="element-3007">
                      <button onClick={() => {
                    dismissAthenaHint();
                    navigate('/workspace/athena-discovery', {
                      state: {
                        openAiPanel: true
                      }
                    });
                  }} className="jua-athena-discover-pill jua-overview-suggestion flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EC4899]/10 hover:bg-[#EC4899]/20 border border-[#EC4899]/25 hover:border-[#EC4899]/40 transition-all text-left group backdrop-blur-sm" data-id="element-3008">
                        <Sparkles size={12} className="text-[#EC4899] flex-shrink-0" data-id="element-3009" />
                        <span className="text-xs text-[#EC4899]/80 group-hover:text-[#EC4899] transition-colors whitespace-nowrap font-medium" data-id="element-3010">
                          What can Athena do?
                        </span>
                      </button>

                      {/* Athena hint callout — above on mobile, left on desktop */}
                      {showAthenaHint && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{
                    zIndex: 100
                  }} data-id="element-3011">
                          <div className={cn('relative rounded-xl border backdrop-blur-xl p-3.5', isLight ? 'bg-white/95 border-slate-200 shadow-[0_0_20px_rgba(236,72,153,0.1),0_4px_20px_rgba(0,0,0,0.08)]' : 'bg-[#0F172A]/90 border-white/[0.08] shadow-[0_0_20px_rgba(236,72,153,0.15),0_4px_20px_rgba(0,0,0,0.2)]')} data-id="element-3012">
                            <button onClick={e => {
                        e.stopPropagation();
                        dismissAthenaHint();
                      }} className={cn('absolute top-2 right-2 p-1 rounded-md transition-colors', isLight ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100' : 'text-white/40 hover:text-white/70 hover:bg-white/10')} data-id="element-3013">
                              <X size={12} data-id="element-3014" />
                            </button>
                            <div className="flex items-start gap-2.5 pr-5" data-id="element-3015">
                              <div className="w-6 h-6 rounded-lg bg-[#EC4899]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-3016">
                                <Sparkles size={12} className="text-[#EC4899]" data-id="element-3017" />
                              </div>
                              <div data-id="element-3018">
                                <p className={cn('text-xs font-semibold mb-1', isLight ? 'text-slate-900' : 'text-white')} data-id="element-3019">
                                  Explore what Athena can do
                                </p>
                                <p className={cn('text-[11px] leading-relaxed', isLight ? 'text-slate-500' : 'text-white/80')} data-id="element-3020">
                                  Take a guided tour of energy forecasts, model
                                  comparisons, and trading signals.
                                </p>
                              </div>
                            </div>
                            {/* Arrow pointing down */}
                            <div className={cn('absolute w-3 h-3 rotate-45', isLight ? 'bg-white/95 border-slate-200' : 'bg-[#0F172A]/90 border-white/[0.08]', '-bottom-[6px] left-1/2 -translate-x-1/2 border-r border-b')} data-id="element-3021" />
                          </div>
                        </div>}
                    </div>
                    {SUGGESTIONS.map(s => <button key={s.label} onClick={() => handleSend(s.prompt)} className={cn('jua-overview-suggestion flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all text-left group backdrop-blur-sm', isLight ? 'bg-black/[0.04] hover:bg-black/[0.08] border-black/[0.08] hover:border-black/[0.15]' : 'bg-white/[0.06] hover:bg-white/[0.10] border-white/[0.10] hover:border-white/[0.18]')} data-id="element-3022">
                        <s.icon size={12} className={cn('flex-shrink-0', isLight ? 'text-slate-500 group-hover:text-slate-700' : 'text-white/50 group-hover:text-white/80')} data-id="element-3023" />
                        <span className={cn('text-xs transition-colors whitespace-nowrap', isLight ? 'text-slate-500 group-hover:text-slate-800' : 'text-white/50 group-hover:text-white/80')} data-id="element-3024">
                          {s.label}
                        </span>
                      </button>)}
                  </div>
                </div>
              </div>

              {/* Workspaces quick-access — pinned to bottom */}
              <div className="w-full mt-auto pt-3 md:pt-6 pb-2 md:pb-[5vh]" data-id="element-3025">
                <div className="flex items-center justify-between mb-3" data-id="element-3026">
                  <div className="flex items-center gap-2 text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-3027">
                    <LayoutGrid size={12} data-id="element-3028" />
                    <span data-id="element-3029">Your Workspaces</span>
                  </div>
                  <button onClick={() => navigate('/workspaces')} className="text-[11px] text-slate-500 hover:text-[#EC4899] transition-colors" data-id="element-3030">
                    View all →
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-3031">
                  {displayWorkspaces.map(ws => <button key={ws.id} onClick={() => {
                setActiveWorkspace(ws.id);
                navigate(`/workspace/${ws.id}`);
              }} className="jua-overview-ws-card flex flex-col items-start gap-1.5 p-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.09] hover:border-white/[0.16] transition-all text-left group backdrop-blur-sm shadow-sm" data-id="element-3032">
                      <div className="flex items-center justify-between w-full" data-id="element-3033">
                        <div className="w-6 h-6 rounded-md bg-[#EC4899]/10 border border-[#EC4899]/20 flex items-center justify-center text-[10px] font-bold text-[#EC4899]" data-id="element-3034">
                          {ws.name.charAt(0)}
                        </div>
                        {ws.favorited && <Star size={10} className="text-amber-400 fill-amber-400" data-id="element-3035" />}
                        {ws.pinned && !ws.favorited && <Pin size={10} className="text-slate-500" data-id="element-3036" />}
                      </div>
                      <span className="text-xs font-medium text-slate-300 group-hover:text-white transition-colors line-clamp-1 leading-tight" data-id="element-3037">
                        {ws.name}
                      </span>
                      <span className="text-[10px] text-slate-600 line-clamp-1" data-id="element-3038">
                        {ws.description}
                      </span>
                    </button>)}
                </div>
              </div>
            </div>
          </div> :
      // Active Chat State
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 md:p-8" data-id="element-3039">
            <div className="max-w-3xl mx-auto space-y-6" data-id="element-3040">
              {activeChat.messages.map((msg, idx) => {
            const isLastAssistant = msg.role === 'assistant' && idx === activeChat.messages.length - 1;
            return <div key={msg.id} className={cn('flex gap-4', msg.role === 'user' ? 'flex-row-reverse' : '')} data-id="element-3041">
                    <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 overflow-hidden', msg.role === 'assistant' ? 'bg-[#EC4899]' : 'bg-slate-700 text-white')} data-id="element-3042">
                      {msg.role === 'assistant' ? <img src="https://cdn.magicpatterns.com/uploads/1xm3GDkQFEZdXmvRqE9BJg/pasted-image.png" alt="Jua" className="w-full h-full object-cover" data-id="element-3043" /> : <User size={16} data-id="element-3044" />}
                    </div>

                    {msg.role === 'user' ? <div className="p-4 rounded-2xl text-sm leading-relaxed max-w-[85%] bg-[#EC4899]/15 text-white/90 border border-[#EC4899]/20 rounded-tr-sm" data-id="element-3045">
                        {renderMessageContent(msg, false)}
                      </div> : <div className="text-sm leading-relaxed max-w-[85%] text-white/80 pt-1" data-id="element-3046">
                        {renderMessageContent(msg, isLastAssistant)}
                      </div>}
                  </div>;
          })}
              <div ref={messagesEndRef} data-id="element-3047" />
            </div>
          </div>}

        {/* Small input bar — only shown when chat is active */}
        {activeChat && <div className="jua-overview-chat-input-bar p-3 md:p-4 bg-[#0B1120] border-t border-slate-800/50" data-id="element-3048">
            <div className="max-w-3xl mx-auto relative" data-id="element-3049">
              <textarea ref={chatTextareaRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={isStreaming ? 'Athena is thinking...' : 'Ask Athena anything...'} rows={1} className="w-full min-h-[48px] max-h-[200px] px-5 pr-12 py-3 rounded-xl bg-white/[0.06] border border-white/[0.10] text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[#EC4899]/40 focus:ring-2 focus:ring-[#EC4899]/10 transition-all shadow-lg resize-none overflow-hidden leading-relaxed backdrop-blur-sm" data-id="element-3050" />
              <button onClick={() => handleSend()} disabled={!inputValue.trim() || isStreaming} className="absolute right-3 bottom-2.5 p-1.5 text-[#EC4899] hover:bg-[#EC4899]/15 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-3051">
                {isStreaming ? <Loader2 size={16} className="animate-spin" data-id="element-3052" /> : <Send size={16} data-id="element-3053" />}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}